import { Component, Inject, OnInit } from '@angular/core';

import {
  PremiumClient,
  PremiumCalculationSearchRequest,
  CoverageRateResponse,
  ApiResponseOfPremiumItemsSAResponse,
  PremiumSelfAdminRequest,
  SpanInfoEnhanced,
  PremiumItemResponse
} from '@benefitsui/app/api/benefits-clients';
import {
  PremiumCalcAccordionType,
  BandedRateCellType,
  CompositeRateCellType,
  TableHeaderTypes,
  InputValueChangeType,
  CalculationData
} from './premium-calc-types';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, debounceTime } from 'rxjs/operators';
import { WINDOW } from '@benefitsui/app/lib/tokens/window';
import { BillingService } from '../billing.service';
import { CurrencyPipe, DecimalPipe } from '@angular/common';
import { UserContextService } from '@benefitsui/app/services/user-context/user-context.service';
import { UserContext } from '@benefitsui/app/services/user-context/user-context.types';
import { Constants } from '../billing-page.types';
import { Defaults } from '@benefitsui/app/shared/defaults/defaults';
import { PolicyService } from '@benefitsui/app/services/policy/policy.service';

declare global {
  interface QsiInterface {
    API: {
      unload: () => void;
      load: () => Promise<void>;
      run: () => PromiseLike<void> | void | null | undefined;
    };
  }

  interface Window {
    QSI?: QsiInterface;
  }
}

@Component({
  selector: 'app-premium-calculation',
  templateUrl: './premium.component.html',
  styleUrls: ['./premium.component.scss']
})
export class PremiumComponent implements OnInit {
  //constants
  CloseText = Constants.CloseText;
  InvoiceCardHeader = 'Invoice details';
  MemberMonth = 'member / month';
  ModalBodyError = 'You are not allowed to change premium calculations.';
  ModalTitle = "Oops, you don't have access";
  PremiumCalculatorHeader = 'Premium calculator';
  RateBasis = '1000';
  StatusCardHeader = 'Premium calculation status';
  SubtotalLabel = 'Subtotal: ';

  TableHeaders: TableHeaderTypes = {
    composite: [
      { text: 'Coverage type', key: 'coverageType', width: '20%' },
      { text: 'Class', key: 'class', width: '15%' },
      { text: 'Number of lives', key: 'numLives', width: '15%' },
      { text: 'Volume', key: 'volume', width: '15%' },
      { text: 'Rate', key: 'formattedRate', width: '20%' },
      { text: 'Amounts', key: 'amounts', align: 'right' }
    ],
    banded: [
      { text: 'Step band by age', key: 'stepBand', width: '35%' },
      { text: 'Number of lives', key: 'numLives', width: '15%' },
      { text: 'Volume', key: 'volume', width: '15%' },
      { text: 'Rate', key: 'formattedRate', width: '20%' },
      { text: 'Amounts', key: 'amounts', align: 'right' }
    ]
  };

  TotalAmountLabel = 'Total calculated amount';

  constructor(
    private premiumClient: PremiumClient,
    private billingService: BillingService,
    private userContextService: UserContextService,
    private policyService: PolicyService,
    @Inject(WINDOW) private window: Window
  ) {
    this.groupName =
      this.billingService.premiumInvoiceData?.billGroupName ||
      Constants.StringEmpty;
    this.groupNumber =
      this.billingService.premiumInvoiceData?.billGroupNumber ||
      Constants.StringEmpty;
    this.invoiceNumber =
      this.billingService.premiumInvoiceData?.invoiceNumber ||
      Constants.StringEmpty;
    this.invoicePeriod = this.billingService.premiumInvoiceData?.invoicePeriod;
    this.premiumId =
      this.billingService.premiumInvoiceData?.premiumID ||
      Constants.StringEmpty;

    // The Payment Status and Invoice Amount field in the Invoice card are being removed for MMP, and will be reintroduced later.
    this.paymentStatus = null;
    this.invoiceAmount = null;
    this.statusColor = null;
    // this.paymentStatus = this.billingService.premiumInvoiceData?.status || Constants.StringEmpty;
    // this.invoiceAmount =
    //   this.formatCurrency(this.billingService.premiumInvoiceData?.amountUI, true) || Constants.StringEmpty;
    // this.statusColor = this.getStatusColor(this.paymentStatus);

    this.reqParams = {
      policyId: undefined,
      invoiceNumber: this.billingService.premiumInvoiceData?.invoiceNumber
    };

    this.userContext = this.userContextService.get();
    this.userType = this.userContext?.symClass;

    // :  Qualtrics survey support
    // this.window.addEventListener('qsi_js_loaded', this.interceptLoaded, false);
    this.interceptLoaded();
  }

  // :  Data
  data$!: Observable<PremiumCalcAccordionType[]>;
  groupName: string;
  groupNumber: string;
  invoiceAmount: string | null;
  invoiceNumber: string;
  invoicePeriod: SpanInfoEnhanced | undefined;
  isLoading = true;
  paymentStatus: string | null;
  premiumId: string;
  reqParams: PremiumCalculationSearchRequest;
  statusColor: string | null;
  userContext: UserContext;
  userType: string;

  calculationUpdated: CalculationData[] = [];
  submittedSuccess = false;

  showPermissionModal = false;
  hasSubmitPermissions = false;
  statePlaceholder = 'Not submitted';
  stateColor = this.getStatusColor(this.statePlaceholder);
  submittedBySA = '--';
  submitDateSA = '--';
  numericCharSet = /[0-9]/;
  numericCharSetWDecimal = /[0-9.]/;
  accordionData: PremiumCalcAccordionType[] = [];
  invoiceTotal = Constants.StringEmpty;
  hasInterceptLoaded = false;

  invoiceLabel = {
    invoicePeriod: 'Invoice period',
    invoiceNumber: 'Invoice number',
    billGroupName: 'Bill group name',
    billGroupNumber: 'Bill group number',
    paymentStatus: 'Payment status',
    estimatedAmount: 'Estimated amount'
  };

  statusLabel = {
    state: 'State',
    submittedBy: 'Changed by',
    submittedDate: 'Changed date'
  };

  private textInput$ = new BehaviorSubject<InputValueChangeType>({
    event: 0,
    tableIndex: 0,
    accordionIndex: 0,
    item: undefined,
    inputType: Defaults.stringEmpty
  });

  private readonly debounceTimeMs = 350;

  onTextChange(input: InputValueChangeType) {
    this.textInput$.next(input);
  }

  ngOnInit(): void {
    const premiumSelfAdminRequest = {
      groupId: '',
      premiumId: this.premiumId,
      invoicePeriod: this.invoicePeriod
    } as PremiumSelfAdminRequest;

    this.data$ = this.premiumClient
      .selfadmin(premiumSelfAdminRequest)
      ?.pipe(map((response) => this.transformData(response)));

    this.textInput$
      .pipe(debounceTime(this.debounceTimeMs))
      .subscribe((inputValue) => this.inputValueChange(inputValue));
    this.hasSubmitPermissions = this.hasPermissions(this.userContext);
  }

  interceptLoaded() {
    if (this.window.QSI?.API !== undefined) {
      this.hasInterceptLoaded = true;
    } else {
      this.hasInterceptLoaded = false;
    }
  }

  transformData(
    response: ApiResponseOfPremiumItemsSAResponse
  ): PremiumCalcAccordionType[] {
    const accordions: PremiumCalcAccordionType[] = [];
    const compositeCells: CompositeRateCellType[] = [];

    const saComment = (response?.data?.saComment ?? '--').replace(
      / *\([^)]*\) */g,
      ''
    );
    const submittedBy = response?.data?.submittedBy;
    const submitDateBy = response?.data?.submittedDate;
    if (submittedBy !== '' && submitDateBy !== '') {
      this.statePlaceholder = 'Submitted';
      this.stateColor = this.getStatusColor(this.statePlaceholder);
      if (submittedBy !== 'SYMT_SERVICE_ACCT') {
        this.submittedBySA = 'Symetra employee';
      } else {
        this.submittedBySA = saComment;
      }
      this.submitDateSA = submitDateBy ?? '--';
    }

    const compositeAccordion: PremiumCalcAccordionType = {
      title: 'Composite rate coverages/services',
      subtotal: 0,
      subtotalUI: this.SubtotalLabel,
      tableHeaders: this.TableHeaders.composite,
      cellRows: [],
      showContent: true
    };

    //allows composite accordion to always appear first on the page
    accordions.push(compositeAccordion);
    let compositeSubtotal = 0;

    response?.data?.premiums?.map((item) => {
      const volume = item?.volume;
      const rate = item?.premiumRate01;
      const rateBasis = this.RateBasis;
      const numLives = item?.units;
      const rowTotal = this.getCellRowTotal(
        rate!,
        rateBasis!,
        numLives,
        volume
      );
      compositeSubtotal += rowTotal;

      compositeCells.push({
        premiumItemId: item.id ?? Constants.StringEmpty,
        coverageType: item.coverageLabel ?? Constants.StringEmpty,
        class: item.policyClassCode ?? Constants.StringEmpty,
        numLives: numLives ?? 0,
        volume: volume,
        formattedRate:
          `${this.formatNumber(rate, '1.0-4')} / ${this.formatRateBasis(rateBasis!)}` ??
          Constants.StringEmpty,
        rate: rate,
        rateBasis: rateBasis,
        amounts: rowTotal,
        showLoader: false
      });

      this.calculationUpdated.push({
        premiumItemId: item.id ?? Constants.StringEmpty,
        numLives: numLives ?? 0,
        volume: volume,
        premiumRate01: rate ?? 0,
        premiumTotal: rowTotal,
        coverageType: item.coverageLabel ?? Constants.StringEmpty,
        class: item.policyClassCode ?? Constants.StringEmpty
      });

      // Banded rates have been removed for MMP and will be reintroduced later.
      // if (item?.bandedRates?.length) {
      //   const banded = this.getBandedAccordions(item.bandedRates);
      //   accordions.push({
      //     title: `${item.coverageType} - class ${this.formatClassNumber(item.classNumber!)}`,
      //     subtotal: banded.accordionTotal,
      //     subtotalUI: `${this.SubtotalLabel}${this.formatCurrency(`${banded.accordionTotal}`)}`,
      //     tableHeaders: this.tableHeaders.banded,
      //     cellRows: banded.result,
      //     showContent: false
      //   });
      // }
    });

    accordions[0].cellRows = compositeCells;
    accordions[0].subtotal = compositeSubtotal;
    accordions[0].subtotalUI = `${this.SubtotalLabel}${this.formatCurrency(`${compositeSubtotal}`)}`;
    this.accordionData = accordions;
    this.invoiceTotal = this.formatCurrency(
      `${this.getCalculatedAmount(accordions)}`
    );

    return accordions;
  }

  getBandedAccordions(input: CoverageRateResponse[]) {
    const result: BandedRateCellType[] = [];
    let accordionTotal = 0;

    input.map((item) => {
      const rowTotal = this.getCellRowTotal(
        item.rate,
        item.rateBasis!,
        item.headCount,
        item.volume
      );
      accordionTotal += rowTotal;
      result.push({
        stepBand:
          item.stepBand === '0-24'
            ? 'Under 25'
            : item.stepBand!.replace('-', ' to '),
        numLives: item.headCount,
        volume: item.volume,
        formattedRate: `${this.formatNumber(item.rate, '1.0-4')} / ${this.formatRateBasis(item.rateBasis!)}`,
        rate: item.rate,
        rateBasis: item.rateBasis,
        amounts: rowTotal,
        showLoader: false
      });
    });
    return { accordionTotal, result };
  }

  getCellRowTotal(
    rate: number,
    rateBasis: string,
    numLives?: number,
    volume?: number
  ) {
    let result = 0;
    const parsedBasis = rateBasis.split('/');
    const basis =
      typeof parseInt(parsedBasis[0]) === 'number'
        ? parseInt(parsedBasis[0])
        : undefined;

    if (volume !== undefined && basis) {
      if (volume > 0) {
        result = (volume * rate) / basis;
      }
    } else if (numLives !== undefined && basis) {
      if (numLives > 0) {
        result = (numLives * rate) / basis;
      }
    } else if (numLives && numLives > 0) {
      result = numLives * rate;
    }
    return result;
  }

  getAccordionSubTotal(input: CompositeRateCellType[] | BandedRateCellType[]) {
    let result = 0;
    input.forEach((item) => {
      result += item.amounts;
    });
    return result;
  }

  getCalculatedAmount(input: PremiumCalcAccordionType[]) {
    let result = 0;
    input.forEach((item) => {
      result += item.subtotal;
    });
    return result;
  }

  formatCurrency(amount: string | undefined, checkEstimated?: boolean): string {
    const currencyPipe = new CurrencyPipe('en-US');
    if (amount === Constants.StringEmpty) {
      return Constants.StringEmpty;
    } else if (checkEstimated && amount?.includes('Estimated')) {
      const num = amount.replace(/[^.0-9]/g, Constants.StringEmpty);
      const returnAmount = currencyPipe.transform(num);
      return returnAmount!;
    } else if (checkEstimated) {
      return Constants.StringEmpty;
    } else {
      return currencyPipe.transform(amount) + Constants.StringEmpty;
    }
  }

  formatNumber(input: number, decimalPlaces?: string) {
    const decimalPipe = new DecimalPipe('en-US');
    if (decimalPlaces) {
      return decimalPipe.transform(input, decimalPlaces);
    } else {
      return decimalPipe.transform(input);
    }
  }

  formatRateBasis(rateBasis: string) {
    const parsedBasis = rateBasis.split('/');
    if (parsedBasis.length === 1) {
      return this.formatNumber(parseInt(parsedBasis[0]));
    } else {
      parsedBasis.forEach((item) => {
        if (typeof parseInt(item) === 'number') {
          this.formatNumber(parseInt(item));
        }
      });
      return parsedBasis.join('/ ');
    }
  }

  //adds spaces between class numbers
  formatClassNumber(input: string) {
    const formattedInput = input.split(',');
    return formattedInput.join(', ');
  }

  getStatusColor(status: string | undefined) {
    switch (status) {
      case 'Unpaid':
        return '#FDD835';
      case 'Not submitted':
        return '#FDD835';
      case 'Paid':
        return '#8ABF2E';
      case 'Submitted':
        return '#8ABF2E';
      default:
        return Constants.StringEmpty;
    }
  }

  inputValueChange(input: InputValueChangeType) {
    if (!input.inputType) return;
    //show spinner
    this.setLoader(input.tableIndex, input.accordionIndex);
    //run calculation
    const numLivesTemp =
      input.inputType === 'numLives' ? input.event : input.item?.numLives;
    const numLives = numLivesTemp?.valueOf() ?? 0;
    const volumeTemp =
      input.inputType === 'volume' ? input.event : input.item?.volume;
    const volume = volumeTemp?.valueOf() ?? 0;
    const rate = input.item?.rate ?? 0;
    const rateBasis = input.item?.rateBasis ?? Constants.StringEmpty;
    const total = this.getCellRowTotal(rate, rateBasis, numLives, volume);
    //update new total
    this.accordionData[input.accordionIndex].cellRows[
      input.tableIndex
    ].amounts = total;
    //update accordion subtotal
    const newSubtotal = this.getAccordionSubTotal(
      this.accordionData[input.accordionIndex].cellRows
    );
    this.accordionData[input.accordionIndex].subtotal = newSubtotal;
    this.accordionData[input.accordionIndex].subtotalUI =
      `${this.SubtotalLabel}${this.formatCurrency(`${newSubtotal}`)}`;
    //update page total
    this.invoiceTotal = this.formatCurrency(
      `${this.getCalculatedAmount(this.accordionData)}`
    );
    //hide spinner
    this.setLoader(input.tableIndex, input.accordionIndex);

    const compositeRateCellrows = this.accordionData[input.accordionIndex]
      .cellRows[input.tableIndex] as CompositeRateCellType;
    const updateddata: CalculationData = {
      premiumItemId:
        compositeRateCellrows.premiumItemId ?? Constants.StringEmpty,
      numLives: +numLives,
      volume: +volume,
      premiumTotal: +compositeRateCellrows.amounts ?? 0,
      premiumRate01: +compositeRateCellrows.rate ?? 0,
      coverageType: compositeRateCellrows.coverageType ?? Constants.StringEmpty,
      class: compositeRateCellrows.class ?? Constants.StringEmpty
    };

    this.updateCalculationData(updateddata);
  }

  setLoader(tableIndex: number, accordionIndex: number) {
    const currLoader =
      this.accordionData[accordionIndex].cellRows[tableIndex].showLoader;
    this.accordionData[accordionIndex].cellRows[tableIndex].showLoader =
      !currLoader;
  }

  goBillingList() {
    this.billingService.setComponent('list');
    this.billingService.clearSelected();
    this.scrollToTop();
  }

  scrollToTop() {
    this.window.scrollTo(0, 0);
  }

  hasPermissions(input: UserContext) {
    const formattedSymClass = input?.symClass.toLowerCase();
    const isPermittedPH =
      formattedSymClass === 'sponsor' &&
      input.policyholderInfo?.isAdmin === true;
    if (formattedSymClass === 'distributor' || isPermittedPH) {
      return true;
    } else {
      return false;
    }
  }

  permissionModalClose() {
    this.showPermissionModal = false;
  }

  onSubmit(input: boolean) {
    if (!input) {
      this.showPermissionModal = true;
    } else {
      const dataToUpdate = this.calculationUpdated;
      const itemsToSubmit: PremiumItemResponse[] = dataToUpdate.map((item) => ({
        ...item,
        id: item.premiumItemId,
        units: item.numLives,
        volume: item.volume,
        premiumTotal: item.premiumTotal,
        premiumRate01: item.premiumRate01,
        coverageType: item.coverageType,
        class: +item.class
      }));
      this.premiumClient
        .submit({
          groupId: '',
          premiumId: this.premiumId,
          groupName: this.policyService.policyName,
          groupNumber: this.policyService.groupNumber,
          billGroupName: this.groupName,
          billGroupNumber: this.groupNumber,
          invoicePeriod: this.invoicePeriod ?? Defaults.spanInfoEnhancedDefault,
          invoiceNumber: this.invoiceNumber,
          totalAmount: 0,
          premiumItems: itemsToSubmit
        })
        .subscribe((response) => {
          if (response.messages && response.messages?.length > 0) {
            console.log('Something wrong: ' + response.messages);
          } else {
            this.submittedSuccess = true;
            this.calculationUpdated = [];
          }

          this.qualtricsSurveyInitiate();
        });
    }
  }

  qualtricsSurveyInitiate() {
    if (this.hasInterceptLoaded) {
      this.window.QSI?.API.unload();
      this.window.QSI?.API.load().then(this.window.QSI?.API.run);
    }
  }

  closeSubmittedPopup() {
    this.submittedSuccess = false;
    this.ngOnInit();
  }

  updateCalculationData(newItem: CalculationData) {
    const indexToUpdate = this.calculationUpdated.findIndex(
      (item) => item.premiumItemId === newItem.premiumItemId
    );
    this.calculationUpdated[indexToUpdate] = newItem;
  }
}
